/* Step4Form.js */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import DateIcon from "../../../imgs/date-icon.svg";
import ArrowIcon from "../../../imgs/arrow-down-icon.svg";
import {
  trackProductLifecycle,
  fetchProductLifecycle,
} from "../../../network/fetch";
import ProgressBar from "../../../components/ProgressBar/ProgressBar";
import TutorialPopup from "../../../components/TutorialPopup/TutorialPopup";
import State from "../State";

const Step4Form = () => {
  const wait = (ms) =>
    new Promise((resolve) => {
      console.log("=== `waiting ${ms}ms` === Step1Form.js === key: 366434 ===");
      console.dir(`waiting ${ms}ms`, { depth: null, colors: true });
      console.log("=================================");
      setTimeout(resolve, ms);
    });

  const navigate = useNavigate();

  const total_steps = 5; // Nombre total d'étapes
  const current_step = 4; // Étape actuelle

  const recyclingDateInputRef = useRef(null);

  const [txUri, setTxUri] = useState(null);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [docIsSaved, setDocIsSaved] = useState(false);

  // État pour les champs du formulaire
  const [formData, setFormData] = useState({
    batteryUniqueId: "",
    batteryUniqueIdConfidential: false,
    recyclingCenter: "",
    recyclingCenterConfidential: false,
    recyclingDate: new Date().toISOString().split("T")[0],
    recyclingDateConfidential: false,
  });

  // Liste des centres de recyclage (vous pouvez la remplacer par des données réelles)
  const recyclingCenters = [
    { id: "center1", name: "Centre de Recyclage 1" },
    { id: "center2", name: "Centre de Recyclage 2" },
    { id: "center3", name: "Centre de Recyclage 3" },
  ];

  // Initialisation des valeurs lors du montage du composant
  useEffect(() => {
    // Récupérer l'identifiant unique de la batterie depuis l'état
    const batteryUniqueId = State.batteryProductData.productId || "";
    setFormData((prevData) => ({
      ...prevData,
      batteryUniqueId: batteryUniqueId,
    }));
  }, []);

  // État pour gérer les erreurs et le chargement
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const dppNextStep = (e) => {
    e.preventDefault();
    navigate("/dpp/step5", {
      replace: true,
    });
    return;
  };

  const handleNextTutorialStep = (e) => {
    e.preventDefault();
    if (tutorialStep < tutorialSteps.length - 1) {
      setTutorialStep(tutorialStep + 1);
    } else {
      setTutorialStep(-1); // Toutes les étapes sont terminées
    }
  };

  const handleCloseTutorial = (e) => {
    e.preventDefault();
    setTutorialStep(-1);
  };

  const [tutorialSteps, setTutorialSteps] = useState([
    {
      content: (
        <>
          <p className="tutorial-title">Encodez vos données</p>
          <p>
            Maintenant vous êtes un centre de recyclage et vous devez recycler
            la batterie en fin de vie d’une voiture électrique.
          </p>
        </>
      ),
      closeLabel: "hidden",
      position: { top: "100px", left: "50%", transform: "translateX(-50%)" },
    },
  ]);

  const submitedStep = [
    {
      content: (
        <>
          <p className="tutorial-title">
            Vos informations ont été enregistrées avec succès
          </p>
          <p>
            Vous avez enregistré les informations relative au recyclage de la
            batterie. Ces données sont désormais accessibles via la blockchain
            pour les autres acteurs de la chaîne. Cliquez sur le bouton
            ci-dessous pour consulter les informations brutes disponibles sur la
            blockchain.
          </p>
        </>
      ),
      closeLabel: "hidden",
      nextLabel: "Étape suivante",
      handleNextTutorialStep: (e) => {
        dppNextStep(e);
      },
      position: {
        top: "320px",
        left: "50%",
        transform: "translateX(-50%)",
        width: "950px",
      },
    },
  ];

  // Gestion du changement d'entrée
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Gestion de la soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const productId = formData.batteryUniqueId; // Récupérer l'ID de la batterie

    const lifecycleData = {
      eventType: "recycling",
      eventDate: new Date().toISOString(),
      recyclingCenter: formData.recyclingCenter,
      recyclingCenterConfidential: formData.recyclingCenterConfidential,
      recyclingDate: formData.recyclingDate,
      recyclingDateConfidential: formData.recyclingDateConfidential,
    };

    try {
      const response = await trackProductLifecycle(productId, lifecycleData);

      if (response.success) {
        console.log("=== response === Step4Form.js ===");
        console.dir(response, { depth: null, colors: true });
        console.log("=================================");

        // Mettre à jour l'état global avec les nouvelles données
        State.recyclingData = { ...response.lifecycleData, ...response.data };

        console.log("=== State === Step4Form.js ===");
        console.dir(State, { depth: null, colors: true });
        console.log("=================================");

        // Gérer le succès
        setTxUri(response.data.tx_uri);
        setDocIsSaved(true);
        setTutorialSteps(submitedStep);
        setTutorialStep(0);

        await wait(2000);
        fetchResults(productId);
      } else {
        // Gérer les erreurs renvoyées par l'API
        setError(
          "Erreur lors du suivi du cycle de vie du produit. Veuillez réessayer."
        );
        console.error("Erreur API:", response.error);
      }
    } catch (err) {
      setError("Une erreur est survenue. Veuillez réessayer.");
      console.error("Erreur de soumission:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async (productId, replay = 0) => {
    const resp2 = await fetchProductLifecycle(productId);
    console.log("=== resp2 === Step4Form.js === key: 226249 ===");
    console.dir(resp2, { depth: null, colors: true });
    console.dir(replay, { depth: null, colors: true });
    console.log("=================================");
    if (replay < 1) {
      await wait(5000);
      fetchResults(productId, replay + 1);
      return;
    }
    return;
  };

  const isFormValid = () => {
    return (
      formData.batteryUniqueId &&
      formData.recyclingCenter &&
      formData.recyclingDate
    );
  };

  return (
    <form onSubmit={handleSubmit} className="container form-2">
      <div className="form-heading-wrapper">
        <ProgressBar total_steps={total_steps} current_step={current_step} />
      </div>
      {tutorialStep >= 0 && (
        <TutorialPopup
          content={tutorialSteps[tutorialStep].content}
          position={tutorialSteps[tutorialStep].position}
          isVisible={tutorialStep >= 0}
          onNext={
            tutorialSteps[tutorialStep].handleNextTutorialStep ||
            handleNextTutorialStep
          }
          onClose={handleCloseTutorial}
          txUrl={txUri}
          showArrow={tutorialSteps[tutorialStep].showArrow || false}
          tmpNextLabel={tutorialSteps[tutorialStep].nextLabel}
          tmpCloseLabel={tutorialSteps[tutorialStep].closeLabel}
        />
      )}
      <div className="fiels-main-wrapper">
        <div className="fields-main">
          <div className="fields-heading">
            <h3>Recyclage de la batterie</h3>
          </div>
          <div className="fields-main-switch-main">
            <div className="spacer"></div>
            <p className="form-switchs-label">Données confidentielles</p>
          </div>

          <div className="fields-main-item">
            <div className="form-field">
              <label htmlFor="batteryUniqueId" className="form-label">
                Identifiant unique de la batterie
              </label>
              <input
                type="text"
                className="form-control"
                id="batteryUniqueId"
                name="batteryUniqueId"
                value={formData.batteryUniqueId}
                readOnly
                required
              />
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="batteryUniqueIdConfidential"
                name="batteryUniqueIdConfidential"
                checked={formData.batteryUniqueIdConfidential}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="fields-main-item">
            <div className="form-field">
              <label htmlFor="recyclingCenter" className="form-label">
                Centre de recyclage
              </label>
              <select
                className="form-control"
                id="recyclingCenter"
                name="recyclingCenter"
                value={formData.recyclingCenter}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Sélectionnez un centre de recyclage
                </option>
                {recyclingCenters.map((center) => (
                  <option key={center.id} value={center.id}>
                    {center.name}
                  </option>
                ))}
              </select>
              <span className="date-icon">
                <img src={ArrowIcon} alt="Icône flèche" />
              </span>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="recyclingCenterConfidential"
                name="recyclingCenterConfidential"
                checked={formData.recyclingCenterConfidential}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="fields-main-item">
            <div className="form-field">
              <label htmlFor="recyclingDate" className="form-label">
                Date de recyclage
              </label>
              <input
                type="date"
                className="form-control"
                id="recyclingDate"
                name="recyclingDate"
                value={formData.recyclingDate}
                onChange={handleChange}
                ref={recyclingDateInputRef}
                required
              />
              <span
                className="date-icon"
                onClick={() =>
                  recyclingDateInputRef.current &&
                  recyclingDateInputRef.current.showPicker &&
                  recyclingDateInputRef.current.showPicker()
                }
              >
                <img src={DateIcon} alt="Icône de date" />
              </span>
            </div>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="recyclingDateConfidential"
                name="recyclingDateConfidential"
                checked={formData.recyclingDateConfidential}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>
        {error && <div className="error-message">{error}</div>}
        <div className="btn-wrapper">
          {!loading && !docIsSaved && (
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!isFormValid() || loading}
            >
              Enregistrer le recyclage
            </button>
          )}
          {loading && (
            <div className="spinner-wrapper">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Chargement...</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export default Step4Form;
